import { Link } from 'react-router-dom';
import { Mail, MessageCircle, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const WA = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`;

export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/diensten', label: t('nav.services') },
    { to: '/gemeentediensten', label: t('nav.municipal') },
    { to: '/projecten', label: t('nav.projects') },
    { to: '/galerij', label: t('nav.gallery') },
    { to: '/over-ons', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const servicesList = t('footer.servicesList');

  return (
    <footer data-testid="footer" className="bg-[#0F172A] text-white">
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 bg-[#FF5F00] rounded-lg flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="font-extrabold text-white text-sm leading-tight">
                  Orange <span className="text-[#FF5F00]">Onderhoud</span>
                </p>
                <p className="text-xs text-slate-400 uppercase tracking-wide">bedrijf</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-5">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-3">
              <a
                href={WA}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="flex items-center justify-center w-10 h-10 bg-[#25D366] rounded-lg text-white hover:bg-[#1EBE58] transition-colors"
              >
                <MessageCircle size={18} />
              </a>
              <a
                href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`}
                data-testid="footer-email"
                className="flex items-center justify-center w-10 h-10 bg-slate-700 rounded-lg text-white hover:bg-slate-600 transition-colors"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {navLinks.map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-slate-300 hover:text-[#FF5F00] text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">
              {t('footer.services')}
            </h4>
            <ul className="space-y-3">
              {Array.isArray(servicesList) && servicesList.map((s, i) => (
                <li key={i}>
                  <Link to="/diensten" className="text-slate-300 hover:text-[#FF5F00] text-sm transition-colors">
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">
              {t('footer.contact')}
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail size={16} className="text-[#FF5F00] mt-0.5 flex-shrink-0" />
                <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`} className="text-slate-300 hover:text-white text-sm transition-colors break-all">
                  {process.env.REACT_APP_CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MessageCircle size={16} className="text-[#FF5F00] mt-0.5 flex-shrink-0" />
                <a href={WA} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white text-sm transition-colors flex items-center gap-1">
                  WhatsApp <ExternalLink size={12} />
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-[#FF5F00] mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 text-sm">Nederland</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-800">
        <div className="px-6 md:px-12 lg:px-24 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-500 text-xs">
            &copy; {year} Orange Onderhoudsbedrijf. {t('footer.rights')}
          </p>
          <Link
            to="/admin/login"
            className="text-slate-600 hover:text-slate-400 text-xs transition-colors"
          >
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
